import type { AttentionItem } from '../dashboardShared/types';

export interface DeriveAdminAttentionInput {
  /** Staged products in `review` — the Staged Products page's "Needs Review" tab. */
  stagedReviewCount: number;
  oldestReviewAgeDays: number | null;
  failedJobsLast24h: number;
  dominantFailedJobVendor: string | null;
  pendingVendorApprovals: Array<{ vendorName: string; skuCount: number }>;
  p95Ms: number | null;
  p95TargetMs: number;
  /** Stores with no paying subscription (`storesWithoutPaidPlan` from `/billing/admin/stats`). */
  storesWithoutPaidPlan: number;
  /** MRR from the same source, so the item never contradicts the MRR tile. */
  monthlyRevenue: number;
}

/**
 * Pure derivation of the admin dashboard's "Needs attention" list.
 * Same rules as `storeHome/deriveStoreAttention.ts`: blocking before warning,
 * an item with zero/absent signal is omitted entirely, every item carries an action.
 */
export function deriveAdminAttention(input: DeriveAdminAttentionInput): AttentionItem[] {
  const items: AttentionItem[] = [];

  if (input.stagedReviewCount > 0) {
    items.push({
      id: 'staged-review',
      severity: 'blocking',
      title: `${input.stagedReviewCount.toLocaleString()} staged product${input.stagedReviewCount !== 1 ? 's need' : ' needs'} review`,
      meta:
        input.oldestReviewAgeDays != null
          ? `Oldest item queued ${input.oldestReviewAgeDays}d ago · nothing publishes to stores until these are reviewed`
          : 'Nothing publishes to stores until these are reviewed',
      action: 'Review staged',
      href: '/app/admin/staged-products',
    });
  }

  if (input.failedJobsLast24h > 0) {
    items.push({
      id: 'failed-jobs',
      severity: 'blocking',
      title: `${input.failedJobsLast24h.toLocaleString()} ingestion job${input.failedJobsLast24h !== 1 ? 's' : ''} failed in the last 24 hours`,
      meta: input.dominantFailedJobVendor ? `Most recent failure: ${input.dominantFailedJobVendor}` : 'Check job logs for details',
      action: 'Retry job',
      href: '/app/admin/jobs',
    });
  }

  if (input.pendingVendorApprovals.length > 0) {
    const names = input.pendingVendorApprovals
      .slice(0, 2)
      .map((v) => `${v.vendorName} (${v.skuCount.toLocaleString()} SKUs)`)
      .join(' and ');
    items.push({
      id: 'vendor-approvals',
      severity: 'warning',
      title: `${input.pendingVendorApprovals.length.toLocaleString()} vendor registration${input.pendingVendorApprovals.length !== 1 ? 's' : ''} awaiting approval`,
      meta: names,
      action: 'Approve vendors',
      href: '/app/admin/brands-vendors/approvals',
    });
  }

  if (input.p95Ms != null && input.p95Ms > input.p95TargetMs) {
    items.push({
      id: 'api-latency',
      severity: 'warning',
      title: `API p95 is ${input.p95Ms.toLocaleString()}ms against a ${input.p95TargetMs}ms target`,
      meta: 'Degraded response time over the recent request window',
      action: 'Inspect system',
      href: '/app/admin/system',
    });
  }

  if (input.storesWithoutPaidPlan > 0) {
    const mrr = Math.round(input.monthlyRevenue);
    items.push({
      id: 'no-billing',
      severity: 'warning',
      title: `${input.storesWithoutPaidPlan.toLocaleString()} store${input.storesWithoutPaidPlan !== 1 ? 's have' : ' has'} no paid plan`,
      meta:
        mrr > 0
          ? `MRR of $${mrr.toLocaleString()} comes from the other stores · these add nothing until they are on a paid plan`
          : 'MRR stays $0 until plans are assigned',
      action: 'Assign plans',
      href: '/app/admin/billing',
    });
  }

  const blocking = items.filter((i) => i.severity === 'blocking');
  const warning = items.filter((i) => i.severity === 'warning');
  return [...blocking, ...warning];
}
